import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';

interface RevisionWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nextVersion: number;
  onContinue: () => void;
}

export function RevisionWarningDialog({
  open,
  onOpenChange,
  nextVersion,
  onContinue,
}: RevisionWarningDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Start a new revision?</AlertDialogTitle>
          <AlertDialogDescription>
            This will start a new revision (v{nextVersion}). The approved version stays in force
            until the new one is approved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onContinue}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface ReturnForChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isPending: boolean;
  onSubmit: (reason: string) => void;
}

export function ReturnForChangesDialog({
  open,
  onOpenChange,
  isPending,
  onSubmit,
}: ReturnForChangesDialogProps) {
  const [reason, setReason] = useState('');

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isPending) {
      setReason('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Return for changes</DialogTitle>
          <DialogDescription>
            The plan will return to draft at the same version. The author will receive this reason.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="return-reason">Reason</Label>
          <Textarea
            id="return-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Describe the required changes"
            disabled={isPending}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(reason.trim())}
            disabled={isPending || !reason.trim()}
          >
            {isPending ? 'Returning...' : 'Return for changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
